import type { Dispatch } from "react";
import type { EditorAction } from "../../editor/reducers";
import { useState } from "react";
import { HeadingLevelFormField } from "../../attributes/heading-level/HeadingLevelFormField";
import { TextFormField } from "../../attributes/text/TextFormField";
import { useRichTextEditor } from "../../rich-text/useRichTextEditor";
import { Document } from "@tiptap/extension-document";
import { Paragraph } from "@tiptap/extension-paragraph";
import { Text } from "@tiptap/extension-text";
import { Bold } from "@tiptap/extension-bold";
import { Italic } from "@tiptap/extension-italic";
import { Strike } from "@tiptap/extension-strike";
import { Underline } from "@tiptap/extension-underline";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import { RegionUpdateForm } from "../RegionUpdateForm";
import { assertTypeName } from "../../client/guards";
import { Language } from "../../rich-text/extension-language";
import {
  useHeadingRegionEditorSuspenseQuery,
  useUpdateHeadingRegionMutation,
} from "../../client/generated";

type Props = {
  headerLevelAttributeId: string;
  textAttributeId: string;
  dispatch: Dispatch<EditorAction>;
};

export function HeadingRegionEditor({
  headerLevelAttributeId,
  textAttributeId,
  dispatch,
}: Props) {
  const { data } = useHeadingRegionEditorSuspenseQuery({
    variables: {
      headerLevelAttributeId,
      textAttributeId,
    },
  });

  const { headerLevelAttr, textAttr } = data;
  assertTypeName(headerLevelAttr?.__typename === "HeaderLevelAttribute");
  assertTypeName(textAttr?.__typename === "TextAttribute");

  const [updateRegion] = useUpdateHeadingRegionMutation();
  const [level, setLevel] = useState(headerLevelAttr.level);
  const editor = useRichTextEditor({
    options: {
      extensions: [
        Document,
        Paragraph,
        Text,
        Bold,
        Italic,
        Strike,
        Underline,
        Language,
        TextStyle,
        Color,
      ],
      content: textAttr.text,
    },
  });

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!editor) return false;

    const result = await updateRegion({
      variables: {
        headerLevelAttributeId,
        textAttributeId,
        level,
        text: editor.getHTML(),
      },
    });

    return (
      !result.errors &&
      !result.data?.updateHeaderLevelAttribute.userErrors?.length &&
      !result.data?.updateTextAttribute.userErrors?.length
    );
  };

  return (
    <RegionUpdateForm onSubmit={onSubmit} dispatch={dispatch}>
      <HeadingLevelFormField
        value={level.toString()}
        onValueChange={(newLevel) => setLevel(parseInt(newLevel))}
        required
      />
      <TextFormField fragment={textAttr.region} editor={editor} />
    </RegionUpdateForm>
  );
}
